/**
 * Dettaglio template — editor di una mail transazionale.
 *
 * ## Cosa non funzionava
 *
 * La pagina leggeva `subject`, `body` e `isActive`: tre attributi che sulla risorsa `templates`
 * **non esistono** (sono `objectText`, `content`, `active`). Non dava errore, semplicemente
 * mostrava oggetto vuoto, corpo vuoto e interruttore spento su qualunque template — anche su
 * quelli che a database hanno un testo — e in salvataggio mandava tre attributi che il server
 * ignora, quindi quello che si scriveva nell'editor non arrivava a destinazione.
 *
 * Inoltre, con la lettura in errore `data` resta `undefined` e il vecchio
 * `if (isLoading || !data) return <PageLoader/>` teneva la pagina in caricamento per sempre:
 * l'avviso d'errore più in basso era codice mai raggiunto. Ora gli stati li governa `MaeAsync`
 * (bloccato → caricamento → errore → vuoto → contenuto).
 *
 * ## Cosa mostra
 *
 * Tutto quello che la risorsa espone, non solo i tre campi editabili: la nota interna che spiega
 * quando parte la mail (`description`), e in sola lettura codice, lingua e categoria — servono a
 * capire *quale* riga si sta modificando quando un template esiste in più copie per tenant.
 */
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import {
  Anchor, Badge, Box, Button, Group, Stack, Switch, Text, TextInput, Textarea,
} from '@mantine/core';
import { IconArrowLeft, IconEye, IconMail, IconPencil } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { notifyError } from '@/shared/lib/notify';
import { MaeAsync } from '@/shared/components/MaeAsync';
import { MaeAdvice } from '@/shared/components/MaeAdvice';
import { MaeCard, MaeField, MaePageHead, MaeShell, MaeTabs } from '@/shared/desktop/MaeShell';
import { mw, mwSectionLabel, MW_CODE_STYLE } from '@/shared/mobile/tokens';
import {
  fetchTemplate, saveTemplate, bodyPreview, isSystem, usedPlaceholders, categoryName,
  type TemplateDraft, type TemplateRow,
} from '@/features/templates/api';
import { TiptapEditor } from '@/features/templates/TiptapEditor';
import { Code } from '@/shared/components/Codice';

export const Route = createFileRoute('/_app/templates/$templateId')({ component: TemplateDetailPage });

/** I campi editabili, presi coi nomi reali della risorsa. */
function draftFrom(t: TemplateRow): TemplateDraft {
  return {
    name: t.name,
    objectText: t.objectText,
    content: t.content,
    description: t.description,
    active: t.active,
  };
}

function TemplateDetailPage() {
  const { templateId } = useParams({ from: '/_app/templates/$templateId' });
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['template', templateId], queryFn: () => fetchTemplate(templateId) });
  const { data } = query;

  /* La bozza si ricarica solo quando cambia il template aperto, non a ogni refetch: dopo un
     salvataggio il refetch tornerebbe qui e sovrascriverebbe eventuali modifiche in corso. */
  const [detail, setDetail] = useState<'editor' | 'preview'>('editor');
  const [draft, setDraft] = useState<TemplateDraft>({});
  const loadedId = data?.id;
  useEffect(() => {
    if (data) setDraft(draftFrom(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedId]);

  /*
   * Template di sistema (`erasable === false`): lo manda un flusso del server, e da v1.149 il
   * nome e' protetto con un 403. Non si disabilita tutta la pagina — contenuto, oggetto, nota e
   * l'interruttore Attivo restano scrivibili, ed e' il motivo per cui questa schermata esiste.
   */
  const system = isSystem(data);

  const save = useMutation({
    mutationFn: () => saveTemplate(templateId, draft, system),
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Template salvato' });
      void qc.invalidateQueries({ queryKey: ['template', templateId] });
      void qc.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (err) => notifyError(err),
  });

  /* Senza questo confronto il bottone Salva è sempre attivo e non si distingue "salvato" da
     "non ho toccato niente": su una mail che parte a tutti i dipendenti la differenza conta. */
  const modified = !!data && Object.entries(draft)
    // Il nome non parte su un template di sistema: non deve nemmeno accendere il bottone Salva.
    .filter(([k]) => !(system && k === 'name'))
    .some(([k, v]) => v !== draftFrom(data)[k as keyof TemplateDraft]);

  const body = draft.content ?? '';
  const placeholders = usedPlaceholders(body);

  return (
    <MaeShell>
      <Anchor component={Link} to="/templates" size="sm" mb={12} display="inline-block">
        <Group gap={4}><IconArrowLeft size={14} /> Torna ai template</Group>
      </Anchor>

      <MaePageHead
        title={data?.name || 'Template'}
        sub={data?.objectText || 'Nessun oggetto impostato: nella casella del destinatario diventa «Mail informativa»'}
        icon={<IconMail size={23} />}
        actions={
          data && (
            <>
              <Switch
                label="Attivo"
                checked={draft.active ?? false}
                onChange={(e) => setDraft((d) => ({ ...d, active: e.currentTarget.checked }))}
              />
              <Button onClick={() => void save.mutate()} loading={save.isPending} disabled={!modified}>
                {modified ? 'Salva' : 'Salvato'}
              </Button>
            </>
          )
        }
      />

      <MaeAsync query={query} loading="Carico il template…" errorTitle="Template non caricato">
        {data && (
          <Stack gap={16}>
            {system && (
              <MaeAdvice
                tone="info"
                title="Template di sistema: il titolo non si cambia"
              >
                Questa mail la manda un flusso del sistema, che la cerca per codice: rinominarla o
                cancellarla la farebbe sparire dall&apos;elenco continuando a partire. Puoi
                modificare <b>oggetto, contenuto, nota interna</b> e l&apos;interruttore
                <b> Attivo</b> — che e&apos; il motivo per cui questa pagina esiste.
              </MaeAdvice>
            )}

            {draft.active === false && (
              <MaeAdvice tone="warning" title="Template non attivo" selfRemedy="Attivalo con l'interruttore qui sopra.">
                Finché resta spento questa mail non viene inviata: al suo posto il destinatario non
                riceve nulla.
              </MaeAdvice>
            )}

            <MaeCard>
              <Text component="div" style={{ ...mwSectionLabel, marginBottom: 14 }}>Identificazione</Text>
              <Stack gap={14}>
                <MaeField
                  label="Nome interno"
                  hint={system
                    ? 'Bloccato: e\u2019 il nome con cui i flussi del sistema riconoscono questo template.'
                    : "Come compare nell'elenco dei template. Non lo vede il destinatario."}
                >
                  <TextInput
                    aria-label="Nome interno"
                    value={draft.name ?? ''}
                    readOnly={system}
                    disabled={system}
                    onChange={(e) => setDraft((d) => ({ ...d, name: e.currentTarget.value }))}
                  />
                </MaeField>
                <MaeField label="Oggetto email" hint="Questo sì: è la riga che il destinatario legge nella casella.">
                  <TextInput
                    aria-label="Oggetto email"
                    value={draft.objectText ?? ''}
                    placeholder="Senza oggetto arriva «Mail informativa»"
                    onChange={(e) => setDraft((d) => ({ ...d, objectText: e.currentTarget.value }))}
                  />
                </MaeField>
                <MaeField label="Quando parte" hint="Nota interna: è l'unico posto dove sta scritto cosa fa scattare questa mail.">
                  <Textarea
                    aria-label="Quando parte"
                    value={draft.description ?? ''}
                    autosize
                    minRows={2}
                    onChange={(e) => setDraft((d) => ({ ...d, description: e.currentTarget.value }))}
                  />
                </MaeField>
              </Stack>

              {/* Sola lettura: dicono *quale* riga si sta modificando. Un template esiste in più
                  copie, una per categoria/tenant, e il codice è la chiave con cui il back-end
                  risolve il template al momento dell'invio. */}
              <Group gap={22} mt={18} wrap="wrap">
                <Meta label="Codice">{data.code || '—'}</Meta>
                <Meta label="Lingua">{(data.language || 'it').toUpperCase()}</Meta>
                <Meta label="Categoria">{categoryName(data) || '—'}</Meta>
              </Group>
            </MaeCard>

            <MaeCard padding={0}>
              <div style={{ padding: 18 }}>
                <MaeTabs
                  value={detail}
                  onChange={setDetail}
                  tabs={[
                    { value: 'editor', label: 'Editor', icon: <IconPencil size={15} /> },
                    { value: 'preview', label: 'Anteprima', icon: <IconEye size={15} /> },
                  ]}
                  mb={14}
                />
                {detail === 'editor' && (
                <div>
                  <TiptapEditor
                    value={body}
                    onChange={(html) => setDraft((d) => ({ ...d, content: html }))}
                  />
                  <Text fz="xs" c="dimmed" mt={10}>
                    Intestazione col logo e footer vengono aggiunti all'invio dal template condiviso:
                    qui si scrive solo il corpo, senza saluti d'apertura né firma.
                  </Text>
                </div>
                )}
                {detail === 'preview' && (
                <div>
                  {body.trim()
                    ? <Box dangerouslySetInnerHTML={{ __html: bodyPreview(body) }} />
                    : <Text fz="sm" c="dimmed">Corpo vuoto: il destinatario riceverebbe una mail bianca.</Text>}
                </div>
                )}
              </div>
            </MaeCard>

            {/* I segnaposto scritti a mano sono la prima causa di mail con «{Foo}» a video: chi
                invia sostituisce solo un elenco chiuso di token, gli altri arrivano al
                destinatario così come sono. Mostrarli rende visibile cosa si è scritto. */}
            <MaeCard>
              <Text component="div" style={{ ...mwSectionLabel, marginBottom: 10 }}>
                Segnaposto usati nel corpo
              </Text>
              {placeholders.length === 0 ? (
                <Text fz="sm" c="dimmed">Nessuno: il testo è identico per tutti i destinatari.</Text>
              ) : (
                <>
                  <Group gap={6}>
                    {placeholders.map((s) => <Code key={s}>{`{${s}}`}</Code>)}
                  </Group>
                  <Text fz="xs" c="dimmed" mt={10}>
                    Sono a graffa singola. Anagrafica utente e azienda è sostituita su ogni mail; i
                    token di un singolo flusso (tipo <Code fz="xs">{'{OTP}'}</Code>) li riempie solo
                    chi manda quella mail — se ne aggiungi uno che nessuno sostituisce, il
                    destinatario lo legge testuale.
                  </Text>
                </>
              )}
            </MaeCard>
          </Stack>
        )}
      </MaeAsync>
    </MaeShell>
  );
}

/** Coppia etichetta/valore in sola lettura. */
function Meta({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Text style={{ fontSize: 'var(--fs-compact)', fontWeight: 500, letterSpacing: '.08em', color: mw.ink300, textTransform: 'uppercase' }}>
        {label}
      </Text>
      <Badge variant="light" color="gray" radius="sm" mt={4} style={{ ...MW_CODE_STYLE }}>
        {children}
      </Badge>
    </div>
  );
}
